(() => {
  const scalarFields = [
    'idempotency_key',
    'title',
    'first_name',
    'last_name',
    'job_title',
    'phone',
    'email',
    'company_legal_name',
    'trading_name',
    'company_registration_number',
    'vat_number',
    'website',
    'country',
    'postal_code',
    'address_line_1',
    'address_line_2',
    'city',
    'region',
    'other_business_type',
    'marketplaces',
    'brands_currently_sold',
    'business_description',
    'honeypot',
  ];
  const arrayFields = ['business_types', 'sales_channels', 'interested_brands'];

  function createIdempotencyKey() {
    if (typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  function normaliseWebsite(input) {
    const value = input.value.trim();
    input.setCustomValidity('');
    if (!value) return;

    const candidate = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    try {
      const url = new URL(candidate);
      if (!['http:', 'https:'].includes(url.protocol) || !url.hostname.includes('.')) throw new Error('Invalid website');
      input.value = url.toString();
    } catch (_error) {
      input.setCustomValidity('Enter a valid website, such as example.com.');
    }
  }

  function validatePhone(input) {
    const value = input.value.trim();
    input.setCustomValidity('');
    if (!value) return;

    const digits = value.replace(/\D/g, '');
    if (!/^[0-9+() .'-]+$/.test(value) || digits.length < 7 || digits.length > 20) {
      input.setCustomValidity('Enter a valid international phone number.');
    }
  }

  function isIreland(value) {
    return /^(ireland|éire|eire|ie)$/i.test(value.trim());
  }

  function updatePostalField(country, postal, label) {
    const ireland = isIreland(country.value);
    label.innerHTML = ireland ? 'Eircode <span aria-hidden="true">*</span>' : 'Eircode / postal code <span aria-hidden="true">*</span>';
    postal.placeholder = ireland ? 'N91 X9CR' : '';

    if (ireland) {
      const compact = postal.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (compact.length === 7) postal.value = `${compact.slice(0, 3)} ${compact.slice(3)}`;
      else postal.value = postal.value.trim().toUpperCase();
    } else {
      postal.value = postal.value.trim();
    }
  }

  function updateReveal(control, target) {
    target.hidden = !control.checked;
    control.setAttribute('aria-expanded', String(control.checked));
    const field = target.querySelector('input');
    if (!control.checked && field) field.value = '';
  }

  function fieldLabel(field) {
    if (field.dataset.errorLabel) return field.dataset.errorLabel;
    const label = field.id ? document.querySelector(`label[for="${CSS.escape(field.id)}"]`) : null;
    return (label?.textContent || field.name || 'This field').replace(/\s*\*\s*$/, '').trim();
  }

  function validationMessage(field) {
    if (field.validity.customError) return field.validationMessage;
    if (field.validity.typeMismatch && field.type === 'email') return 'Enter a valid email address.';
    if (field.validity.valueMissing && field.name === 'privacy_acknowledged') {
      return 'Confirm that you have read the Privacy & Cookie Statements.';
    }
    if (field.validity.valueMissing) return 'This field is required.';
    return 'Check this field and try again.';
  }

  function showSummary(summary, list, entries, firstField) {
    list.replaceChildren();
    for (const entry of entries) {
      const item = document.createElement('li');
      if (entry.field?.id) {
        const link = document.createElement('a');
        link.href = `#${entry.field.id}`;
        link.textContent = `${entry.label}: ${entry.message}`;
        item.append(link);
      } else {
        item.textContent = entry.message;
      }
      list.append(item);
    }
    summary.hidden = entries.length === 0;
    if (!entries.length) return true;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    summary.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
    (firstField || summary).focus({ preventScroll: true });
    return false;
  }

  function showBrowserValidation(form, summary, list) {
    const invalidFields = Array.from(form.querySelectorAll('input:invalid, select:invalid, textarea:invalid')).filter(
      (field) => !field.closest('[hidden]'),
    );
    const entries = invalidFields.map((field) => {
      field.setAttribute('aria-invalid', 'true');
      return { field, label: fieldLabel(field), message: validationMessage(field) };
    });
    return showSummary(summary, list, entries, invalidFields[0]);
  }

  function showServerError(form, summary, list, response) {
    const entries = Array.isArray(response.fields)
      ? response.fields.map(({ field: name, message }) => {
        const namedItem = form.elements.namedItem(name);
        const control = namedItem instanceof RadioNodeList ? namedItem[0] : namedItem;
        if (control instanceof HTMLElement) control.setAttribute('aria-invalid', 'true');
        return {
          field: control instanceof HTMLElement ? control : null,
          label: control instanceof HTMLElement ? fieldLabel(control) : name.replaceAll('_', ' '),
          message,
        };
      })
      : [{ field: null, label: '', message: response.error || 'The application could not be submitted. Please try again.' }];
    return showSummary(summary, list, entries, entries.find((entry) => entry.field)?.field);
  }

  function payloadFromForm(form) {
    const formData = new FormData(form);
    const payload = {};
    for (const field of scalarFields) payload[field] = String(formData.get(field) || '');
    for (const field of arrayFields) payload[field] = formData.getAll(field).map(String);
    payload.privacy_acknowledged = formData.get('privacy_acknowledged') === 'true';
    payload.marketing_opt_in = formData.get('marketing_opt_in') === 'true';
    return payload;
  }

  document.querySelectorAll('[data-retailer-application]').forEach((section) => {
    const form = section.querySelector('[data-retailer-form]');
    const success = section.querySelector('[data-application-success]');
    if (!form || !success) return;

    const idempotencyKey = form.querySelector('[data-idempotency-key]');
    const website = form.querySelector('[data-website]');
    const phone = form.querySelector('[data-phone]');
    const country = form.querySelector('[data-country]');
    const postal = form.querySelector('[data-postal-code]');
    const postalLabel = form.querySelector('[data-postal-label]');
    const honeypot = form.querySelector('[data-honeypot]');
    const summary = form.querySelector('[data-validation-summary]');
    const validationList = form.querySelector('[data-validation-list]');
    const submitButton = form.querySelector('[data-submit-button]');
    const submitLabel = form.querySelector('[data-submit-label]');
    idempotencyKey.value = createIdempotencyKey();

    form.querySelectorAll('[data-reveal-control]').forEach((control) => {
      const target = form.querySelector(`[data-reveal-target="${control.dataset.revealControl}"]`);
      if (!target) return;
      updateReveal(control, target);
      control.addEventListener('change', () => updateReveal(control, target));
    });

    website.addEventListener('blur', () => normaliseWebsite(website));
    phone.addEventListener('blur', () => validatePhone(phone));
    country.addEventListener('input', () => updatePostalField(country, postal, postalLabel));
    postal.addEventListener('blur', () => updatePostalField(country, postal, postalLabel));

    form.querySelectorAll('input, select, textarea').forEach((field) => {
      field.addEventListener('input', () => {
        field.removeAttribute('aria-invalid');
        if (field === website || field === phone) field.setCustomValidity('');
      });
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      normaliseWebsite(website);
      validatePhone(phone);
      updatePostalField(country, postal, postalLabel);

      if (honeypot.value.trim()) honeypot.setCustomValidity('Please leave this field blank.');
      if (!showBrowserValidation(form, summary, validationList)) return;

      submitButton.disabled = true;
      form.setAttribute('aria-busy', 'true');
      submitLabel.textContent = 'Submitting securely…';

      try {
        const response = await fetch(form.action, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(payloadFromForm(form)),
        });
        const result = await response.json().catch(() => ({ ok: false, error: 'The submission service returned an unexpected response.' }));
        if (!response.ok || !result.ok) {
          showServerError(form, summary, validationList, result);
          return;
        }

        success.querySelector('[data-success-reference]').textContent = result.reference;
        form.hidden = true;
        success.hidden = false;
        success.focus();
      } catch (_error) {
        showServerError(form, summary, validationList, { error: 'A network error interrupted submission. Your entries are still here; please try again.' });
      } finally {
        submitButton.disabled = false;
        form.removeAttribute('aria-busy');
        submitLabel.textContent = 'Submit retailer application';
      }
    });
  });
})();
